import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Slider,
  TextField,
  Typography,
} from '@mui/material';
import { Save } from 'lucide-react';
import { useNotification } from '../../hooks/useNotification';
import {
  type HomeAppearanceConfig,
  useHomeAppearance,
  useUpdateHomeAppearance,
} from '../../services/homeAppearance.service';

export const HomeAppearanceCard: React.FC = () => {
  const notification = useNotification();
  const { data, isLoading, error } = useHomeAppearance();
  const updateMutation = useUpdateHomeAppearance();
  const [draft, setDraft] = useState<HomeAppearanceConfig | null>(null);

  useEffect(() => {
    if (data?.config) {
      setDraft(data.config);
    }
  }, [data]);

  const hasChanges = useMemo(() => {
    if (!draft || !data?.config) return false;
    return (
      draft.backgroundTheme !== data.config.backgroundTheme ||
      draft.backgroundThemeIntensity !== data.config.backgroundThemeIntensity
    );
  }, [draft, data]);

  const handleSave = async () => {
    if (!draft) return;
    try {
      await updateMutation.mutateAsync(draft);
      notification.success('Home page background updated');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update home page background';
      notification.error(message);
    }
  };

  return (
    <Paper
      elevation={0}
      className="mt-6 rounded-xl bg-white p-6 dark:bg-slate-800"
      sx={{
        boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
      }}
    >
      <Box className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <Box>
          <Typography variant="h6" className="font-semibold text-gray-900 dark:text-slate-100">
            Home page background
          </Typography>
          <Typography variant="body2" className="mt-1 text-gray-600 dark:text-slate-400">
            Animated background shown to visitors on the public home page. Users whose plan does not
            include dynamic themes lose it after signing in.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={
            updateMutation.isPending ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <Save size={20} />
            )
          }
          onClick={handleSave}
          disabled={!hasChanges || updateMutation.isPending}
          sx={{
            backgroundColor: '#3B82F6',
            '&:hover': {
              backgroundColor: '#2563EB',
            },
            '&:disabled': {
              backgroundColor: '#94a3b8',
            },
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>

      {isLoading ? (
        <Box className="flex items-center justify-center py-8">
          <CircularProgress size={24} className="text-blue-500" />
        </Box>
      ) : error || !data || !draft ? (
        <Alert severity="error" className="mt-4">
          Failed to load home page background
        </Alert>
      ) : (
        <Box className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2">
          <TextField
            select
            size="small"
            fullWidth
            label="Background theme"
            value={draft.backgroundTheme}
            onChange={(event) =>
              setDraft((previous) =>
                previous ? { ...previous, backgroundTheme: event.target.value } : null
              )
            }
          >
            {data.options.backgroundThemes.map((themeId) => (
              <MenuItem key={themeId} value={themeId}>
                {themeId.charAt(0).toUpperCase() + themeId.slice(1)}
              </MenuItem>
            ))}
          </TextField>

          <Box className="flex flex-col justify-center">
            <Typography
              variant="body2"
              className={
                draft.backgroundTheme === 'none'
                  ? 'text-gray-400 dark:text-slate-500'
                  : 'text-gray-700 dark:text-slate-300'
              }
            >
              Intensity: {draft.backgroundThemeIntensity}
            </Typography>
            <Slider
              size="small"
              min={data.options.intensity.min}
              max={data.options.intensity.max}
              step={5}
              value={draft.backgroundThemeIntensity}
              valueLabelDisplay="auto"
              disabled={draft.backgroundTheme === 'none'}
              onChange={(_, value) => {
                if (typeof value === 'number') {
                  setDraft((previous) =>
                    previous ? { ...previous, backgroundThemeIntensity: value } : null
                  );
                }
              }}
              className="mt-1"
            />
          </Box>
        </Box>
      )}
    </Paper>
  );
};
